import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { RotateCcw } from 'lucide-react';

// ─── Animations ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`from{opacity:0;transform:scale(0.9)}to{opacity:1;transform:scale(1)}`;
const matched = keyframes`0%{transform:scale(1)}40%{transform:scale(1.12)}100%{transform:scale(1)}`;
const shake = keyframes`0%,100%{transform:translateX(0)}25%{transform:translateX(-6px)}75%{transform:translateX(6px)}`;
const confettiFall = keyframes`
  0%{opacity:1;transform:translate(0,0)rotate(0)}
  100%{opacity:0;transform:translate(var(--dx),120px)rotate(360deg)}
`;
const bounce = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}`;
const pulse = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.05)}`;

// ─── Types ───────────────────────────────────────────────────────────────────

interface MatchCard {
  id: string;
  text: string;
  pairId: number;
  side: 'en' | 'vi';
}

type CardState = 'idle' | 'selected' | 'matched' | 'wrong';

// ─── Styled ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  animation: ${fadeIn} 0.4s ease;
  font-family: 'Segoe UI', sans-serif;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  @media(max-width: 500px) { gap: 7px; }
`;

const Card = styled.button<{ state: CardState; side: 'en' | 'vi' }>`
  padding: 9px 7px;
  border-radius: 14px;
  border: 2.5px solid;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  min-height: 64px;
  line-height: 1.3;
  transition: all 0.18s;
  word-break: break-word;
  text-align: center;

  ${({ state, side }) => {
    if (state === 'idle') return css`
      border-color: ${side === 'en' ? '#c7d2fe' : '#fde68a'};
      background: ${side === 'en' ? '#eef2ff' : '#fffbeb'};
      color: ${side === 'en' ? '#3730a3' : '#78350f'};
      &:hover { transform: translateY(-3px); box-shadow: 0 6px 16px rgba(0,0,0,0.12); }
    `;
    if (state === 'selected') return css`
      border-color: ${side === 'en' ? '#667eea' : '#f59e0b'};
      background: ${side === 'en' ? '#667eea' : '#f59e0b'};
      color: #fff;
      transform: translateY(-3px);
      box-shadow: 0 6px 20px ${side === 'en' ? 'rgba(102,126,234,0.4)' : 'rgba(245,158,11,0.4)'};
    `;
    if (state === 'matched') return css`
      border-color: #22c55e;
      background: #dcfce7;
      color: #15803d;
      animation: ${matched} 0.4s ease;
      cursor: default;
    `;
    if (state === 'wrong') return css`
      border-color: #ef4444;
      background: #fee2e2;
      color: #b91c1c;
      animation: ${shake} 0.3s ease;
    `;
  }}
`;

const StatsRow = styled.div`
  display: flex; gap: 12px; justify-content: center;
  margin-bottom: 16px; flex-wrap: wrap;
`;

const Stat = styled.div<{ color: string }>`
  background: ${p => p.color}15;
  border: 1.5px solid ${p => p.color}40;
  color: ${p => p.color};
  border-radius: 20px; padding: 6px 16px;
  font-size: 13px; font-weight: 700;
`;

const ResultBox = styled.div`
  text-align: center; padding: 40px 20px;
  background: #fff; border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
`;

const ResultEmoji = styled.div`
  font-size: 64px; animation: ${bounce} 1s infinite;
`;

const ResultTitle = styled.div`
  font-size: 24px; font-weight: 800; color: #1a1a2e; margin: 12px 0 8px;
`;

const ResultSub = styled.div`
  font-size: 14px; color: #94a3b8; margin-bottom: 24px;
`;

const RestartBtn = styled.button`
  background: linear-gradient(90deg, #667eea, #764ba2);
  color: #fff; border: none; border-radius: 12px;
  padding: 13px 32px; font-size: 15px; font-weight: 700;
  cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
  transition: all 0.2s;
  &:hover { opacity: 0.9; transform: translateY(-2px); }
`;

const Confetti = styled.div<{ color: string; delay: number; left: number }>`
  position: fixed; top: 0; left: ${p => p.left}vw;
  width: 9px; height: 9px;
  background: ${p => p.color};
  border-radius: 2px;
  pointer-events: none; z-index: 9999;
  animation: ${confettiFall} ${() => 1.2 + Math.random() * 0.8}s linear ${p => p.delay}ms forwards;
  --dx: ${() => (Math.random() - 0.5) * 200}px;
`;

// ─── Sound ───────────────────────────────────────────────────────────────────

function playBeep(freq: number, dur: number, type: OscillatorType = 'sine') {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = freq; osc.type = type;
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    osc.start(); osc.stop(ctx.currentTime + dur);
  } catch {}
}

function playMatch() { playBeep(880, 0.12); setTimeout(() => playBeep(1100, 0.12), 100); }
function playWrong() { playBeep(200, 0.35, 'sawtooth'); }
function playDone() { [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => playBeep(f, 0.2), i * 90)); }

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  items: any[];
  onGameComplete?: (pct: number) => void;
}

const ReviewMatchGame: React.FC<Props> = ({ items, onGameComplete }) => {
  const [cards, setCards] = useState<MatchCard[]>([]);
  const [states, setStates] = useState<Record<string, CardState>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [matchedCount, setMatchedCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [done, setDone] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const init = useCallback(() => {
    const pool = items; // use all vocab items, no limit
    const enCards: MatchCard[] = pool.map((item, i) => ({ id: `en-${i}`, text: item.front, pairId: i, side: 'en' }));
    const viCards: MatchCard[] = pool.map((item, i) => ({ id: `vi-${i}`, text: item.back,  pairId: i, side: 'vi' }));

    // Shuffle each column independently
    const shuffle = <T,>(arr: T[]) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    const shuffledEn = shuffle(enCards);
    const shuffledVi = shuffle(viCards);

    // Interleave: build grid col-by-col so left col = EN, right col = VI
    const grid: MatchCard[] = [];
    for (let i = 0; i < pool.length; i++) {
      grid.push(shuffledEn[i], shuffledVi[i]);
    }

    setCards(grid);
    const initStates: Record<string, CardState> = {};
    [...shuffledEn, ...shuffledVi].forEach(c => { initStates[c.id] = 'idle'; });
    setStates(initStates);
    setSelected(null);
    setMatchedCount(0);
    setWrongCount(0);
    setDone(false);
    setShowConfetti(false);
  }, [items]);

  useEffect(() => { init(); }, [init]);

  // Report result when game ends
  useEffect(() => {
    if (done && onGameComplete) {
      const total = items.length;
      const accuracy = Math.round((total / (total + wrongCount)) * 100);
      onGameComplete(accuracy);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const tap = (card: MatchCard) => {
    if (states[card.id] === 'matched') return;
    if (selected === card.id) { // deselect
      setSelected(null);
      setStates(s => ({ ...s, [card.id]: 'idle' }));
      return;
    }

    if (!selected) {
      setSelected(card.id);
      setStates(s => ({ ...s, [card.id]: 'selected' }));
      return;
    }

    // Second pick
    const selCard = cards.find(c => c.id === selected)!;
    const isMatch = selCard.pairId === card.pairId && selCard.side !== card.side;

    if (isMatch) {
      playMatch();
      const nm = matchedCount + 1;
      setMatchedCount(nm);
      setStates(s => ({ ...s, [selected]: 'matched', [card.id]: 'matched' }));
      setSelected(null);
      if (nm === items.length) {
        setTimeout(() => { setDone(true); playDone(); setShowConfetti(true); setTimeout(() => setShowConfetti(false), 3000); }, 300);
      }
    } else {
      playWrong();
      setWrongCount(w => w + 1);
      setStates(s => ({ ...s, [selected]: 'wrong', [card.id]: 'wrong' }));
      setTimeout(() => {
        setStates(s => ({
          ...s,
          [selected]: states[selected] === 'matched' ? 'matched' : 'idle',
          [card.id]: 'idle',
        }));
        setSelected(null);
      }, 700);
    }
  };

  if (items.length < 2) return (
    <div style={{ textAlign: 'center', color: '#bbb', padding: 40 }}>Cần ít nhất 2 từ vựng để chơi ghép thẻ.</div>
  );

  const total = items.length;

  if (done) {
    const accuracy = Math.round((total / (total + wrongCount)) * 100);
    return (
      <div>
        {showConfetti && Array.from({ length: 28 }).map((_, i) => (
          <Confetti key={i} color={['#667eea','#f59e0b','#22c55e','#ec4899','#38bdf8'][i % 5]} delay={i * 70} left={Math.random() * 100} />
        ))}
        <ResultBox>
          <ResultEmoji>🎯</ResultEmoji>
          <ResultTitle>Hoàn thành! Ghép hết {total} cặp!</ResultTitle>
          <ResultSub>Số lần ghép sai: {wrongCount} · Chính xác: {accuracy}%</ResultSub>
          <RestartBtn onClick={init}><RotateCcw size={15} /> Chơi lại</RestartBtn>
        </ResultBox>
      </div>
    );
  }

  return (
    <Wrap>
      <StatsRow>
        <Stat color="#667eea">💡 {total} cặp</Stat>
        <Stat color="#22c55e">✅ Đã ghép: {matchedCount}</Stat>
        <Stat color="#ef4444">❌ Sai: {wrongCount}</Stat>
      </StatsRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 6 }}>
        <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#6366f1', letterSpacing: 1, textTransform: 'uppercase' }}>🔤 Tiếng Anh</div>
        <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#d97706', letterSpacing: 1, textTransform: 'uppercase' }}>🇻🇳 Tiếng Việt</div>
      </div>
      <Grid>
        {cards.map(card => (
          <Card
            key={card.id}
            state={states[card.id] || 'idle'}
            side={card.side}
            onClick={() => tap(card)}
            disabled={states[card.id] === 'matched'}
          >
            {card.text}
          </Card>
        ))}
      </Grid>
    </Wrap>
  );
};

export default ReviewMatchGame;
